import React from "react";
import { gql, useMutation, useSubscription } from "@apollo/client";

import { CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";
import {
  Inbox as InventoryIcon,
  PackageUp as RemoveIcon,
} from "mdi-material-ui";

const INVENTORY_FRAGMENT = gql`
  fragment SoundShapingInventories_Inventory on Inventory {
    id
    box
    column
    row

    sku
  }
`;

const SOUND_SHAPING_FRAGMENT = gql`
  fragment SoundShapingInventories_SoundShaping on SoundShaping {
    id
    sku

    order {
      id
    }

    inventories {
      id

      ...SoundShapingInventories_Inventory
    }
  }

  ${INVENTORY_FRAGMENT}
`;

const SUBSCRIPTION = gql`
  subscription {
    inventoryChangeStream {
      id
      operationType
      inventory {
        id

        ...SoundShapingInventories_Inventory
      }
    }
  }

  ${INVENTORY_FRAGMENT}
`;

const MUTATION = gql`
  mutation ($inventoryId: ID!, $orderId: ID!, $message: String!) {
    RemoveInventory(inventoryId: $inventoryId) {
      id
    }

    CommentOrder(orderId: $orderId, message: $message) {
      order {
        id
      }
    }
  }
`;

export default function SoundShapingInventories({ soundShaping }) {
  useSubscription(SUBSCRIPTION, {
    onSubscriptionData: ({ client, subscriptionData }) => {
      const data = client.readFragment({
        id: `SoundShaping:${soundShaping.id}`,
        fragment: SOUND_SHAPING_FRAGMENT,
        fragmentName: "SoundShapingInventories_SoundShaping",
      });

      const updateInventories = (inventories) => {
        client.cache.writeFragment({
          id: `SoundShaping:${soundShaping.id}`,
          fragment: SOUND_SHAPING_FRAGMENT,
          fragmentName: "SoundShapingInventories_SoundShaping",
          data: {
            ...data,
            inventories,
          },
        });
      };

      const addInventory = (inventory) => {
        updateInventories(
          data.inventories
            .filter(({ id }) => id !== inventory.id)
            .concat([inventory])
        );
      };

      const removeInventory = (inventoryId) => {
        updateInventories(
          data.inventories.filter(({ id }) => id !== inventory.id)
        );
      };

      const change = subscriptionData.data.inventoryChangeStream;
      switch (change.operationType) {
        case "insert":
          if (change.inventory.sku === soundShaping.sku)
            addInventory(change.inventory);
          break;

        case "delete":
          removeInventory(change.id);
          break;

        default:
          return;
      }
    },
  });

  const inventory = soundShaping.inventories?.[0];
  const location = inventory?.box + ":" + inventory?.column + inventory?.row;

  const [deleteInventory, { loading }] = useMutation(MUTATION, {
    variables: {
      inventoryId: inventory?.id,
      orderId: soundShaping.order.id,
      message: `À prendre dans l’inventaire : ${location}`,
    },
  });

  const handleClick = React.useCallback(() => {
    deleteInventory({
      update: (client) => {
        const data = client.readFragment({
          id: `SoundShaping:${soundShaping.id}`,
          fragment: SOUND_SHAPING_FRAGMENT,
          fragmentName: "SoundShapingInventories_SoundShaping",
        });

        client.writeFragment({
          id: `SoundShaping:${soundShaping.id}`,
          fragment: SOUND_SHAPING_FRAGMENT,
          fragmentName: "SoundShapingInventories_SoundShaping",
          data: {
            ...data,
            inventories: data.inventories.filter(
              ({ id }) => id !== inventory?.id
            ),
          },
        });
      },
    });
  }, [deleteInventory, soundShaping.id, inventory?.id]);

  if (!inventory) return null;

  return (
    <Tooltip
      placement="top"
      title={
        <Stack spacing={1} direction="row" alignItems="center">
          <span>{`Disponible dans l’inventaire : ${location}`}</span>

          <IconButton
            size="small"
            color="inherit"
            onClick={handleClick}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              <RemoveIcon />
            )}
          </IconButton>
        </Stack>
      }
    >
      <InventoryIcon />
    </Tooltip>
  );
}

SoundShapingInventories.fragments = {
  soundShaping: SOUND_SHAPING_FRAGMENT,
};
